// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	ChannelUnreadState,
	ChannelUnreadStateInput,
} from '@app/features/app/components/layout/utils/ChannelUnreadState';
import {MessageNotifications} from '@fluxer/constants/src/NotificationConstants';
import {assign, initialTransition, type SnapshotFrom, setup} from 'xstate';

interface ChannelUnreadMachineContext extends ChannelUnreadStateInput {}

type ChannelUnreadMachineEvent = {
	type: 'channelUnread.updated';
	input: ChannelUnreadStateInput;
};

type ChannelUnreadStateValue = 'disabled' | 'onlyMentions' | 'allMessages' | 'legacy';

function getUnreadStateValue(snapshot: ChannelUnreadSnapshot): ChannelUnreadStateValue {
	switch (snapshot.value) {
		case 'disabled':
			return 'disabled';
		case 'onlyMentions':
			return 'onlyMentions';
		case 'allMessages':
			return 'allMessages';
		default:
			return 'legacy';
	}
}

const channelUnreadStateMachine = setup({
	types: {} as {
		context: ChannelUnreadMachineContext;
		events: ChannelUnreadMachineEvent;
		input: ChannelUnreadStateInput;
	},
	actions: {
		applyInput: assign(({event}) => {
			if (event.type !== 'channelUnread.updated') return {};
			return event.input;
		}),
	},
	guards: {
		unreadBadgesDisabled: ({context}) => context.unreadBadgesLevel === MessageNotifications.NO_MESSAGES,
		unreadBadgesOnlyMentions: ({context}) => context.unreadBadgesLevel === MessageNotifications.ONLY_MENTIONS,
		unreadBadgesAllMessages: ({context}) => context.unreadBadgesLevel === MessageNotifications.ALL_MESSAGES,
	},
}).createMachine({
	id: 'channelUnread',
	context: ({input}) => input,
	initial: 'routing',
	states: {
		routing: {
			always: [
				{guard: 'unreadBadgesDisabled', target: 'disabled'},
				{guard: 'unreadBadgesOnlyMentions', target: 'onlyMentions'},
				{guard: 'unreadBadgesAllMessages', target: 'allMessages'},
				{target: 'legacy'},
			],
		},
		disabled: {
			on: {'channelUnread.updated': {target: 'routing', actions: 'applyInput'}},
		},
		onlyMentions: {
			on: {'channelUnread.updated': {target: 'routing', actions: 'applyInput'}},
		},
		allMessages: {
			on: {'channelUnread.updated': {target: 'routing', actions: 'applyInput'}},
		},
		legacy: {
			on: {'channelUnread.updated': {target: 'routing', actions: 'applyInput'}},
		},
	},
});

type ChannelUnreadSnapshot = SnapshotFrom<typeof channelUnreadStateMachine>;

function createChannelUnreadSnapshot(input: ChannelUnreadStateInput): ChannelUnreadSnapshot {
	return initialTransition(channelUnreadStateMachine, input)[0];
}

function selectChannelUnreadState(snapshot: ChannelUnreadSnapshot): ChannelUnreadState {
	const context = snapshot.context;
	const hasUnreadMessages = context.hasUnread;
	const rawHasMentions = context.mentionCount > 0;
	switch (getUnreadStateValue(snapshot)) {
		case 'disabled':
			return {
				hasUnreadMessages,
				hasMentions: false,
				isHighlight: false,
				shouldShowUnreadIndicator: false,
				isUnreadIndicatorMuted: false,
				hasVisibleUnread: false,
			};
		case 'onlyMentions':
			return {
				hasUnreadMessages,
				hasMentions: rawHasMentions,
				isHighlight: rawHasMentions,
				shouldShowUnreadIndicator: hasUnreadMessages,
				isUnreadIndicatorMuted: hasUnreadMessages,
				hasVisibleUnread: rawHasMentions || hasUnreadMessages,
			};
		case 'allMessages':
			return {
				hasUnreadMessages,
				hasMentions: rawHasMentions,
				isHighlight: rawHasMentions || hasUnreadMessages,
				shouldShowUnreadIndicator: hasUnreadMessages,
				isUnreadIndicatorMuted: false,
				hasVisibleUnread: rawHasMentions || hasUnreadMessages,
			};
		case 'legacy': {
			const shouldShowUnreadIndicator =
				hasUnreadMessages && (!context.isMuted || context.showFadedUnreadOnMutedChannels);
			const hasMentions = rawHasMentions;
			return {
				hasUnreadMessages,
				hasMentions,
				isHighlight: rawHasMentions || (hasUnreadMessages && !context.isMuted),
				shouldShowUnreadIndicator,
				isUnreadIndicatorMuted: shouldShowUnreadIndicator && context.isMuted,
				hasVisibleUnread: hasMentions || shouldShowUnreadIndicator,
			};
		}
	}
}

export function resolveChannelUnreadState(input: ChannelUnreadStateInput): ChannelUnreadState {
	return selectChannelUnreadState(createChannelUnreadSnapshot(input));
}
